import { Button } from '@foam/buoy/src/components/Button/Button';
import { Icon } from '@foam/buoy/src/components/Icon/Icon';
import { MagicButton } from '@foam/buoy/src/components/MagicButton/MagicButton';
import { toast } from '@foam/buoy/src/components/Toast/Toast';
import { Tooltip } from '@foam/buoy/src/components/Tooltip/Tooltip';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Editable } from '~/components/Editable/Editable';
import { FEATURE_FLAGS } from '~/constants';
import {
  ONBOARDING_HOOKS_ACTION_TYPES,
  ONBOARDING_HOOKS_TRACK_ID,
} from '~/constants/extension';
import { MEDIA_KIT_TEXTS } from '~/constants/mediaKitTexts';
import { navigateWithTransition } from '~/core/utils/viewTransition/navigateWithTransition';
import { useGetMediaKit } from '~/hooks/api/media-kits/get';
import { useUpdateMediaKit } from '~/hooks/api/media-kits/update';
import { useConversationStarterAction } from '~/hooks/useConversationStarterAction';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { useRouteHistory } from '~/store/routeHistory.store';

import { useCopyMKLink } from './hooks/useCopyMKLink';
import { useDeleteMediaKitStore } from './modals/DeleteConfirmation/store';

export const Topbar = () => {
  const router = useRouter();
  const { isEnabled: isShareToLinkedInEnabled } = useFeature(
    FEATURE_FLAGS.foamMediaKitsShareToLinkedIn,
  );
  const id = router.query.id as string;
  const { history } = useRouteHistory();
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  const mediaKit = useGetMediaKit({ id });
  const updateMediaKit = useUpdateMediaKit({ id });
  const { copyMkLink } = useCopyMKLink(id);
  const { openModalToDeleteMK } = useDeleteMediaKitStore();
  const { handleConversationStarterAction } = useConversationStarterAction();

  const [mediaKitName, setMediaKitName] = useState(mediaKit.data?.name || '');
  const [isSaved, setIsSaved] = useState(false);

  const { isEnabled: isPrimaryMediaKitEnabled } = useFeature(
    FEATURE_FLAGS.foamPrimaryMediaKit,
  );

  const { isEnabled: isOnboardingHooksEnabled } = useFeature(
    FEATURE_FLAGS.foamMediaKitsOnboardingHooks,
  );

  const tooltipText = useMemo(() => {
    if (!mediaKit.data?.is_public) return undefined;
    return isPrimaryMediaKitEnabled
      ? MEDIA_KIT_TEXTS.PRIMARY_DELETE
      : MEDIA_KIT_TEXTS.DEFAULT_DELETE;
  }, [mediaKit.data?.is_public, isPrimaryMediaKitEnabled]);

  useEffect(() => {
    if (mediaKit.data?.name) {
      setMediaKitName(mediaKit.data.name);
    }
  }, [mediaKit.data?.name]);

  useEffect(() => {
    if (isSaved) {
      saveTimeoutRef.current = setTimeout(() => {
        setIsSaved(false);
      }, 3000);
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [isSaved]);

  const handleBackNavigation = useCallback(() => {
    if (history.length < 2) {
      navigateWithTransition(router, '/media-kits');
      return;
    }

    const previousPage = history[history.length - 2];
    const currentPage = history[history.length - 1];

    if (previousPage && previousPage !== currentPage) {
      navigateWithTransition(router, previousPage);
    } else {
      navigateWithTransition(router, '/media-kits');
    }
  }, [history, router]);

  const handleSave = useCallback(
    (value: string) => {
      if (!value || value.length < 3) {
        toast('Media kit name should be at least 3 characters long', {
          variant: 'alert',
        });
        setMediaKitName(mediaKit.data?.name || '');
        return;
      }

      updateMediaKit.mutate(
        { name: value },
        {
          onSuccess: () => setIsSaved(true),
          onError: () => {
            toast('Failed to save media kit name. Please try again.', {
              variant: 'alert',
            });
            setMediaKitName(mediaKit.data?.name || '');
          },
        },
      );
    },
    [mediaKit.data?.name, updateMediaKit],
  );

  const handleShareToLinkedIn = useCallback(() => {
    const shareUrl = `${window.location.origin}/shared/media-kits/${id}?utm_source=linkedin`;
    const title = mediaKitName;
    const summary = `Check out ${title} on Foam 😎`;

    const linkedInUrl = `https://www.linkedin.com/shareArticle/?url=${encodeURIComponent(
      shareUrl,
    )}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(
      summary,
    )}&text=${encodeURIComponent(summary)}&mini=true`;

    // Open as popup window
    const width = 550;
    const height = 450;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    window.open(
      linkedInUrl,
      'Share to LinkedIn',
      `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${width}, height=${height}, top=${top}, left=${left}`,
    );
  }, [id, mediaKitName]);

  return (
    <div className="border-b border-neutral-100 bg-neutral-0 px-6 pb-6 pt-8">
      <div className="flex flex-wrap justify-between">
        <div className="flex max-w-[80%] flex-1 items-center gap-4">
          <Button
            size="md"
            variant="ghost"
            onClick={handleBackNavigation}
            aria-label="Go back"
            data-track-id="media-kit-builder-topbar-back-btn"
            kind="icon-only"
          >
            <Icon name="ArrowLeft" />
          </Button>
          {mediaKit.data?.name ? (
            <Editable
              defaultValue={mediaKit.data.name}
              isDisabled={mediaKit.isLoading || updateMediaKit.isLoading}
              onChange={setMediaKitName}
              onSubmit={handleSave}
              trackId="media-kit-name-editor"
              value={mediaKitName}
              isSaved={isSaved}
            />
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {isOnboardingHooksEnabled && mediaKit.data?.id && (
            <MagicButton
              size="lg"
              magicVariant="default"
              animationLoop="once"
              iconStart="Magic"
              data-track-id={ONBOARDING_HOOKS_TRACK_ID.recommendedBrandPartners}
              onClick={() => {
                handleConversationStarterAction(
                  ONBOARDING_HOOKS_ACTION_TYPES.recommendedBrandPartnersHook,
                  {
                    id: mediaKit.data?.id || '',
                    fullName: mediaKit.data?.name || '',
                    slug: mediaKit.data?.id || '',
                  },
                  {
                    feature: 'onboarding-hooks',
                    action: 'recommendedBrandPartners',
                  },
                );
              }}
            >
              Recommended Brand Partners
            </MagicButton>
          )}

          {isShareToLinkedInEnabled ? (
            <Button
              size="lg"
              variant="secondary"
              iconStart="LinkedInDark"
              onClick={handleShareToLinkedIn}
              data-track-id="media-kit-builder-topbar--linkedin-share-btn"
              style={{
                backgroundColor: '#0273b1',
              }}
            >
              Share to LinkedIn
            </Button>
          ) : (
            <Tooltip content={tooltipText} placement="top">
              <Button
                size="lg"
                variant="tertiary"
                onClick={() => openModalToDeleteMK(id)}
                data-track-id="media-kit-builder-topbar--delete-btn"
                disabled={mediaKit.data?.is_public}
              >
                Delete
              </Button>
            </Tooltip>
          )}

          <Button
            size="lg"
            variant="primary"
            iconStart="Copy"
            onClick={copyMkLink}
            data-track-id="media-kit-builder-topbar--copy-link-btn"
          >
            Copy link
          </Button>
        </div>
      </div>
    </div>
  );
};
